import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, ChevronLeft, ExternalLink, History, KanbanSquare, Users } from "lucide-react";
import type { PmMicroScenarioView } from "@arc/contracts";
import { Button, LinkButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import type { PmView } from "./pm-nav.js";
import { PmDrehbuch } from "./PmDrehbuch.js";
import { PmKanban } from "./PmKanban.js";
import { PmMeeting } from "./PmMeeting.js";
import { PmTaskDialog } from "./PmTaskDialog.js";
import { PmVerlauf, isNewActivity } from "./PmVerlauf.js";
import { trainingStage } from "./pm-labels.js";
import { StatusChip } from "./StatusChip.js";
import { PmError, recordPmVisit, usePmMicroScenario, usePmMsActivity } from "./usePmBoard.js";

/**
 * One micro-scenario (ADR-055 phase 5, mockup "2a"): a side menu (Zurück · Drehbuch · Tasks · Meeting · Verlauf, plus
 * "Ansehen" when there is something to see, D10) and the selected view — Drehbuch, the Kanban, the meeting list, the
 * Verlauf. `taskId` (from the URL) opens a task's details over whichever view is open.
 *
 * Opening an MS records the visit (phase 6b) and keeps the PREVIOUS one for this session: "neu" in the Verlauf and the
 * menu's "N neu" compare against it. Holding it in state matters — the next refetch already carries this visit.
 */
export function PmMsView({
  session,
  meId,
  msId,
  view,
  taskId,
  onView,
  onOpenTask,
  onCloseTask,
  onBack,
}: {
  session: AuthSession;
  meId: number;
  msId: number;
  view: PmView;
  taskId: number | null;
  onView: (view: PmView) => void;
  onOpenTask: (taskId: number) => void;
  onCloseTask: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const q = usePmMicroScenario(session, msId);
  const activity = usePmMsActivity(session, msId);
  const [visit, setVisit] = useState<{ msId: number; previous: string | null } | null>(null);
  const recorded = useRef<number | null>(null);
  useEffect(() => {
    // Once per MS opened (StrictMode runs effects twice in development; a second write would report THIS visit as the
    // previous one and nothing would ever be "new").
    if (recorded.current === msId) return;
    recorded.current = msId;
    recordPmVisit(msId).then(
      (v) => setVisit({ msId, previous: v.previousVisitAt }),
      () => setVisit({ msId, previous: null }),
    );
  }, [msId]);
  const lastVisitAt = visit?.msId === msId ? visit.previous : undefined;
  const newCount = lastVisitAt ? (activity.data ?? []).filter((a) => isNewActivity(a, lastVisitAt, meId)).length : 0;

  if (q.isPending) {
    return (
      <p role="status" className="text-muted-foreground p-6 text-sm">
        {t("pmBoard.loading")}
      </p>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof PmError && q.error.status === 404;
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <BackButton onBack={onBack} />
        <p role="alert" className="text-destructive text-sm">
          {missing ? t("pmBoard.msNotFound") : t("pmBoard.msLoadFailed")}
        </p>
        {missing ? null : (
          <Button variant="outline" size="sm" onPress={() => void q.refetch()} isDisabled={q.isFetching}>
            {t("pmBoard.tryAgain")}
          </Button>
        )}
      </div>
    );
  }

  const data = q.data;
  return (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      <SideNav data={data} view={view} newCount={newCount} onView={onView} onBack={onBack} />
      {/* Without bottom padding on the Kanban: its sideways scrollbar sits on the panel's lower edge. */}
      <div className={cn("bg-muted/40 relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-4 py-6 sm:px-8", view === "tasks" && "md:pb-0")}>
        {view === "drehbuch" ? (
          <PmDrehbuch session={session} meId={meId} data={data} onBack={onBack} />
        ) : view === "tasks" ? (
          <PmKanban session={session} data={data} onOpenTask={onOpenTask} />
        ) : view === "meeting" ? (
          <PmMeeting session={session} data={data} onOpenTask={onOpenTask} />
        ) : (
          <PmVerlauf session={session} meId={meId} data={data} lastVisitAt={lastVisitAt} onOpenTask={onOpenTask} />
        )}
      </div>
      {taskId !== null ? <PmTaskDialog session={session} meId={meId} data={data} taskId={taskId} onClose={onCloseTask} /> : null}
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="outline" onPress={onBack} className="self-start">
      <ChevronLeft aria-hidden="true" />
      {t("pmBoard.nav.back")}
    </Button>
  );
}

function SideNav({
  data,
  view,
  newCount,
  onView,
  onBack,
}: {
  data: PmMicroScenarioView;
  view: PmView;
  newCount: number;
  onView: (v: PmView) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const flagged = data.tasks.filter((x) => x.flaggedForMeeting).length;
  const stage = trainingStage(t, data.ms);
  const items: Array<{ id: PmView; icon: typeof BookOpen; count?: number }> = [
    { id: "drehbuch", icon: BookOpen },
    { id: "tasks", icon: KanbanSquare, count: data.tasks.length },
    { id: "meeting", icon: Users, count: flagged },
    { id: "verlauf", icon: History },
  ];
  return (
    <nav aria-label={t("pmBoard.nav.label")} className="border-border flex shrink-0 flex-col gap-1 border-b px-3 py-4 md:w-60 md:border-r md:border-b-0 md:py-6">
      <div className="mb-3 px-1">
        <BackButton onBack={onBack} />
      </div>
      <ul className="m-0 flex list-none flex-row flex-wrap gap-1 p-0 md:flex-col">
        {items.map(({ id, icon: Icon, count }) => {
          const current = view === id;
          return (
            <li key={id}>
              <Button
                variant={current ? "secondary" : "ghost"}
                aria-current={current ? "page" : undefined}
                onPress={() => onView(id)}
                className={cn("h-9 w-full justify-start gap-2.5 px-3", current && "font-semibold")}
              >
                <Icon aria-hidden="true" />
                <span className="flex-1 text-left">{t(`pmBoard.nav.${id}`)}</span>
                {count !== undefined && count > 0 ? (
                  <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-xs tabular-nums">{count}</span>
                ) : null}
                {id === "verlauf" && newCount > 0 ? <StatusChip tone="attention">{t("pmBoard.verlauf.newCount", { count: newCount })}</StatusChip> : null}
              </Button>
            </li>
          );
        })}
      </ul>
      {stage && data.ms.trainingUrl ? (
        <div className="mt-4 flex flex-col gap-1.5 px-1">
          {/* `no-underline`: Preflight is excluded, so the UA's link underline would otherwise run under the label. */}
          <LinkButton variant="accent" href={data.ms.trainingUrl} target="_blank" rel="noopener noreferrer" className="no-underline">
            <ExternalLink aria-hidden="true" />
            {t("pmBoard.nav.open3d")}
          </LinkButton>
          <span className="text-muted-foreground text-center text-xs">{t("pmBoard.nav.open3dHint", { stage })}</span>
        </div>
      ) : null}
    </nav>
  );
}
